import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  MapPin,
  Network,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  businessNetworkEventPath,
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
} from "./businessNetworkEvents";
import useBusinessNetworkEvents from "./useBusinessNetworkEvents";

const EVENT_POSTER_PATH = "/urban-unity-business-network-launch.png";
const EVENT_MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=140a%20Byng%20Street%2C%20Isle%20of%20Dogs%2C%20London%20E14%209AR";

export default function BusinessNetworkLaunch() {
  const { nextEvent, loading } = useBusinessNetworkEvents();
  const nextPath = nextEvent ? businessNetworkEventPath(nextEvent) : "/business-network";
  const nextRegistrationOpen = nextEvent?.registrationOpen === true;

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <section
        className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#E63946]/25 via-white/5 to-blue-500/10 p-5 sm:p-6 md:p-10"
        aria-labelledby="business-launch-title"
      >
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff9aa2]">
          Previous event · Urban Unity Network × E3 Coaching
        </p>
        <h1 id="business-launch-title" className="mt-3 max-w-4xl text-4xl font-black tracking-tight md:text-6xl">
          Urban Unity Business Network Launch
        </h1>
        <p className="mt-5 text-2xl font-bold text-white">Connect locally. Grow together.</p>
        <p className="mt-2 max-w-3xl text-lg leading-8 text-white/80">
          Local businesses came together to build relationships, share opportunities and mark the launch of the Urban Unity Business Network, delivered in partnership with E3 Coaching.
        </p>
        <div className="mt-7 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <Users className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Connect</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <Network className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Collaborate</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <TrendingUp className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Grow</p>
          </div>
        </div>
      </section>

      <section
        className="rounded-3xl border border-amber-300/25 bg-amber-300/10 p-5 text-center md:p-8"
        aria-labelledby="business-launch-complete-title"
      >
        <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-100">September 2026 launch</p>
        <h2 id="business-launch-complete-title" className="mt-2 text-3xl font-black">
          This event has now taken place.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl leading-7 text-white/75">
          The Business Network now runs as a monthly programme. Visit the permanent hub or join the next published event.
        </p>
        <Link
          to={nextEvent && nextRegistrationOpen ? `${nextPath}?source=business-network-launch-page` : "/business-network"}
          className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#E63946] px-5 py-3 font-black text-white hover:bg-[#d62f3d] sm:w-auto"
        >
          {nextEvent && nextRegistrationOpen
            ? "Register for the next Business Network event"
            : loading
            ? "View the Business Network"
            : "View upcoming Business Network events"}
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </section>

      <section
        className="grid gap-6 rounded-3xl border border-white/10 bg-white/5 p-5 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:p-8"
        aria-labelledby="business-event-details-title"
      >
        <a
          href={EVENT_POSTER_PATH}
          className="group block self-start overflow-hidden rounded-2xl bg-white shadow-2xl shadow-black/20"
          aria-label="Open the full Urban Unity Business Network Launch event poster"
        >
          <img
            src={EVENT_POSTER_PATH}
            alt="Urban Unity Business Network Launch poster with the event date, time and Phoenix Heights venue details"
            width="1198"
            height="1313"
            decoding="async"
            className="h-auto w-full transition duration-300 group-hover:scale-[1.01]"
          />
        </a>
        <div className="self-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff9aa2]">Historical event details</p>
          <h2 id="business-event-details-title" className="mt-2 text-3xl font-black md:text-4xl">Launch event</h2>
          <dl className="mt-6 grid gap-4">
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <CalendarDays className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div><dt className="font-bold">Date</dt><dd className="mt-1 text-white/75"><time dateTime="2026-09-30">Wednesday 30 September 2026</time></dd></div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <Clock3 className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div><dt className="font-bold">Time</dt><dd className="mt-1 text-white/75"><time dateTime="17:30">5:30 PM</time>–<time dateTime="19:00">7:00 PM</time></dd></div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div>
                <dt className="font-bold">Venue</dt>
                <dd className="mt-1 text-white/75">Phoenix Heights Community Centre<br />140a Byng Street, Isle of Dogs, London E14 9AR</dd>
                <a href={EVENT_MAP_URL} target="_blank" rel="noreferrer" className="mt-2 inline-flex font-bold text-[#ff9aa2] underline-offset-4 hover:underline">View on Google Maps</a>
              </div>
            </div>
          </dl>
        </div>
      </section>

      {nextEvent ? (
        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8" aria-labelledby="launch-next-event-title">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff9aa2]">Next event</p>
          <h2 id="launch-next-event-title" className="mt-2 text-2xl font-black md:text-3xl">{nextEvent.title}</h2>
          <p className="mt-3 text-white/75">
            {formatBusinessNetworkEventDate(nextEvent)} · {formatBusinessNetworkEventTime(nextEvent)}
          </p>
          <Link
            to={nextRegistrationOpen ? `${nextPath}?source=business-network-launch-page` : nextPath}
            className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#E63946] px-5 py-3 font-black text-white hover:bg-[#d62f3d] sm:w-auto"
          >
            {nextRegistrationOpen ? "Register free" : "View event"}
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </section>
      ) : null}

      <p className="text-center text-sm leading-6 text-white/65">
        Questions? Email{" "}
        <a href="mailto:info@urbanunitynetwork.com?subject=Urban%20Unity%20Business%20Network" className="font-bold text-[#ff9aa2] underline-offset-4 hover:underline">
          info@urbanunitynetwork.com
        </a>.
      </p>
    </div>
  );
}
